import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'antd'
import dayjs from 'dayjs'
import Actions from '../../../actions'
import $t from '../../../i18n'
import config from '../../../config.json'
import AppointmentConfirmationDialog from '../../pages/dialogs/AppointmentConfirmationDialog'
import { clinicName, getClinicAction } from '../../../utils/clinicsUtils'
import { clinicTypes, clinicTypesShort, routes } from '../../../consts'
import {
  getAppointmentMemberId,
  validateChangeAppointment,
} from '../../../utils/appointmentsUtils'
import { RemoveForKids } from '../../../utils/familyUtils'
import FamilyMemberLabel from '../FamilyMemberLabel'
import Card from '../Card'
import NotInsuredLabel from '../NotInsuredLabel'

import './AppointmentStatusCard.css'

const AppointmentStatusCard = props => {
  const [
    shouldShowCancelAppointmentPopup,
    setShouldShowCancelAppointmentPopup,
  ] = useState(false)

  const [
    shouldShowRescheduleAppointmentPopup,
    setShouldShowRescheduleAppointmentPopup,
  ] = useState(false)

  const eta = useSelector(state => state.appointments.eta)
  const activeMembers = useSelector(state => state.account?.activeMembers)

  const dispatch = useDispatch()

  const { appointment } = props

  const closeRescheduleAppointmentPopup = () => {
    setShouldShowRescheduleAppointmentPopup(false)
  }

  const showRescheduleAppointmentPopup = () => {
    setShouldShowRescheduleAppointmentPopup(true)
  }

  const closeCancelAppointmentPopup = () => {
    setShouldShowCancelAppointmentPopup(false)
  }

  const showCancelAppointmentPopup = () => {
    setShouldShowCancelAppointmentPopup(true)
  }

  const onRescheduleAppointment = (id, antidote_clinic, isFollowUp) => {
    closeRescheduleAppointmentPopup()
    const appointmentMemberId = getAppointmentMemberId(appointment)
    const appointmentMember = activeMembers?.find(
      member => member.id === appointmentMemberId
    )
    dispatch(
      Actions.createAction(Actions.RESCHEDULE_APPOINTMENT_REQUESTED, {
        appointmentId: id,
        clinic: antidote_clinic,
        appointmentMember,
      })
    )
    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        eventName: Actions.RESCHEDULE_APPOINTMENT_REQUESTED,
        data: { appointmentId: id },
      })
    )

    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        eventName: isFollowUp
          ? `${clinicTypesShort[antidote_clinic]}_FOLLOW_UP_USER_RESCHEDULE`
          : `${clinicTypesShort[antidote_clinic]}_INITIAL_USER_RESCHEDULE`,
      })
    )
  }

  const onCancelAppointment = ({ id, isFollowUp, antidote_clinic }) => {
    closeCancelAppointmentPopup()
    dispatch(
      Actions.createAction(Actions.CANCEL_APPOINTMENT_REQUESTED, {
        appointmentId: id,
      })
    )

    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        eventName: Actions.CANCEL_APPOINTMENT_REQUESTED,
        data: { appointmentId: id },
      })
    )

    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        eventName: isFollowUp
          ? `${clinicTypesShort[antidote_clinic]}_FOLLOW_UP_USER_CANCEL`
          : `${clinicTypesShort[antidote_clinic]}_INITIAL_USER_CANCEL`,
      })
    )
  }

  const getNotInsuredLabels = participants => {
    // the patientId contains p_{id}. we need to instract only the id
    const patientId = participants?.[0]?.id?.split('p_')?.[1]
    return (
      <NotInsuredLabel
        className="appointment-card-label"
        patientId={patientId}
      />
    )
  }

  const getParticipants = participants => {
    // the patientId contains p_{id}. we need to instract only the id
    const patientId = participants?.[0]?.id?.split('p_')?.[1]
    return (
      <FamilyMemberLabel
        className="appointment-card-participants"
        key={patientId}
        patientId={patientId}
      />
    )
  }

  const handlePaymentFromVisit = () => {
    dispatch(Actions.createAction(Actions.NAVIGATED_TO, routes.PAYMENT_PLANS))
  }

  const navigateToBeforeVisit = () => {
    dispatch(
      Actions.createAction(Actions.SELECTED_APPOINTMENT_CHANGED, appointment)
    )
  }

  const getItemTitle = appointment => {
    if (
      appointment?.antidote_clinic?.includes(clinicTypes.PRIMARY) &&
      !appointment?.is_created_by_patient
    ) {
      return $t('pages.waitingRoom.appointments.clinicianFollowup')
    }
    return $t('pages.waitingRoom.appointments.itemTitle')
  }

  const renderUi = () => {
    const {
      id,
      schduled_time,
      antidote_clinic,
      participants,
      status,
      last_cancel_time,
      is_clinic_follow_up,
    } = appointment

    // if the user is without family, we don't show the family manage label
    const isWithoutFamily = !activeMembers?.length

    const isChangeableAppointment = validateChangeAppointment(last_cancel_time)
    const isPendingAppointment = status === 'pending'
    const isResceduleVisible = getClinicAction(
      antidote_clinic,
      'rescedule',
      is_clinic_follow_up
    )?.isVisible
    const cancelAlertKey = getClinicAction(
      antidote_clinic,
      'cancel',
      is_clinic_follow_up
    )?.alertKey
    const resceduleAlertKey = getClinicAction(
      antidote_clinic,
      'rescedule',
      is_clinic_follow_up
    )?.alertKey

    const formatDisplayTime = dayjs(schduled_time)
      .local()
      .format(config.scheduleDateFormat)
    const etaInMinutes = $t('pages.waitingRoom.in', {
      eta: dayjs.duration(Math.floor(eta), 'minutes').asMinutes(),
    })

    return (
      <Card className="status-card row">
        <div className="info-container column streach">
          {!isWithoutFamily && getNotInsuredLabels(participants)}
          <div className="first-row row">
            {!isWithoutFamily && getParticipants(participants)}
            <span
              className={
                isWithoutFamily
                  ? 'duration-text item no-family'
                  : 'duration-text item'
              }
            >
              {schduled_time && formatDisplayTime}
              {!schduled_time && eta && etaInMinutes}
            </span>
          </div>
          <div className="second-row row streach wrap">
            <div className="doctor-visit-text item streach">
              {getItemTitle(appointment) +
                '  |  ' +
                clinicName(antidote_clinic, is_clinic_follow_up)}
            </div>
            {RemoveForKids(
              isChangeableAppointment && (
                <div className="buttons-container item streach">
                  <span
                    className="cancel-text link"
                    onClick={showCancelAppointmentPopup}
                  >
                    {$t('dialogs.cancelAppointment.buttonText')}
                  </span>
                  {isResceduleVisible && (
                    <span className="or-text">{$t('dialogs.orText')}</span>
                  )}
                  {isResceduleVisible && (
                    <span
                      className="reschedule-text link"
                      onClick={showRescheduleAppointmentPopup}
                    >
                      {$t('dialogs.rescheduleAppointment.buttonText')}
                    </span>
                  )}
                </div>
              )
            )}
            {RemoveForKids(
              isPendingAppointment && (
                <div className="pending-app item streach">
                  <Button
                    className="payment-btn pills"
                    type="primary"
                    onClick={() => handlePaymentFromVisit()}
                  >
                    {$t('dialogs.payment.buttonText')}
                  </Button>
                </div>
              )
            )}
          </div>
        </div>
        <AppointmentConfirmationDialog
          isCancelRequest={true}
          isPendingAppointment={isPendingAppointment}
          confirmText={
            cancelAlertKey
              ? $t(`dialogs.cancelAppointment.${cancelAlertKey}`, {
                  clinic: clinicName(antidote_clinic),
                })
              : null
          }
          handleOk={() =>
            onCancelAppointment({
              id,
              isFollowUp: is_clinic_follow_up,
              antidote_clinic,
            })
          }
          closePopup={closeCancelAppointmentPopup}
          isVisible={shouldShowCancelAppointmentPopup}
          title={$t('dialogs.cancelAppointment.title')}
        />
        <AppointmentConfirmationDialog
          isPendingAppointment={isPendingAppointment}
          shouldContactSupport={!!resceduleAlertKey}
          supportMessage={$t(
            `dialogs.AppointmentConfirmationDialog.${resceduleAlertKey}`,
            { clinic: clinicName(antidote_clinic) }
          )}
          handleOk={() =>
            onRescheduleAppointment(id, antidote_clinic, is_clinic_follow_up)
          }
          closePopup={closeRescheduleAppointmentPopup}
          isVisible={shouldShowRescheduleAppointmentPopup}
          title={$t('dialogs.rescheduleAppointment.title')}
        />
        {RemoveForKids(
          <div
            className="appointment-more-details"
            onClick={navigateToBeforeVisit}
          >
            <i className="icon-arrow-bold-right" />
          </div>
        )}
      </Card>
    )
  }

  return renderUi()
}

export default AppointmentStatusCard
